/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Перебор объекта</h2>
    <p className={styles.description}>
      По умолчанию объект не является итерируемым, то есть по нему нельзя пройти циклом for или for...of как по массиву
      или строке. Для того чтобы перебрать объект существует несколько специальных конструкций, некоторые необходимо
      знать так как это часть языка, а некоторые используются в повседневной работе.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Цикл for...in</h3>
      <CopyBlock
        text={`for (key in object) {
  // statements
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Перебирает ключи объекта</li>
        <li className={styles.list__item}>Можно удалять свойства во время перебора</li>
        <li className={styles.list__item}>Нельзя использовать для перебора массивов</li>
      </ul>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

// Перебор ключей объекта hotel
for (const key in hotel) {
  console.log('Key: ', key);
}

/*
 * Key: name
 * Key: stars
 * Key: capacity
 */`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Для того чтобы получить значение свойства с таким ключом во время итерации, используется синтаксис квадратных
        скобок, так как ключ всегда строка,.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

for (const key in hotel) {
  console.log('Value: ', hotel[key]);
}

/*
 * Value: "Resort Hotel"
 * Value: 5
 * Value: 100
 */`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Object.keys(), Object.values(), Object.entries()</h3>
      <p className={styles.description}>
        Все объекты происходят от конструктора <code className={styles.code}>Object</code>. У него есть несколько очень
        полезных методов для работы с объектами.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>Object.keys(obj)</code> - вернет массив, с ключами объекта{' '}
          <code className={styles.code}>obj</code> в виде строк.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>Object.values(obj)</code> - вернет массив, со значениями свойств объекта{' '}
          <code className={styles.code}>obj</code> .
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>Object.entries(obj)</code> - вернет массив записей, каждым элементом которого
          будет еще один массив из 2-х элементов, ключа и значения этого ключа из объекта
          <code className={styles.code}>obj</code>.
        </li>
      </ul>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

const keys = Object.keys(hotel); // ["name", "stars", "capacity"]
const values = Object.values(hotel); // ["Resort Hotel", 5, 100]
const entries = Object.entries(hotel); // [Array(2), Array(2), Array(2)]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Скомбинировав результат <code className={styles.code}>Object.keys()</code> и цикл{' '}
        <code className={styles.code}>for...of</code> мы можем перебрать объект. На самом деле мы перебираем массив
        ключей, а потом просто обращаемся к свойству объекта с таким ключом.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

const keys = Object.keys(hotel); // ["name", "stars", "capacity"]

for (const key of keys) {
  console.log('Value: ', hotel[key]);
}
/*
 * Value: Resort Hotel
 * Value: 5
 * Value: 100
 */`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        А можно использовать результат <code className={styles.code}>Object.entries()</code> который вернет массив
        записей с ключами и значениями свойств объекта.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

/*
 * Посмотрите в консоли из каких двух элементов состоит каждый подмассив.
 * Первый элемент это ключ свойства, второй это значение.
 */
const entries = Object.entries(hotel);

/*
 * На каждой итерации по entries выберем первый элемент подмассива (ключ)
 * в переменную key, а второй (значение) в переменную value
 */
for (const entry of entries) {
  const key = entry[0];
  const value = entry[1];

  console.log(\`$ {key}: $ {value}\`);
}

/*
 * name: Resort Hotel
 * stars: 5
 * capacity: 100
 */`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Допустим перед нами стоит задача посчитать общее количество продуктов в объекте формата{' '}
        <code className={styles.code}>имя-продукта: количество</code>. Тогда подойдет метод{' '}
        <code className={styles.code}>Object.values()</code> для того чтобы получить массив всех значений и потом удобно
        их сложить.{' '}
      </p>
      <CopyBlock
        text={`const goods = {
  apples: 6,
  grapes: 3,
  bread: 4,
  cheese: 7,
};

const values = Object.values(goods); // [6, 3, 4, 7]

let total = 0;

for (const value of values) {
  total += value;
}

console.log(total); // 20`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
